// neo_cogym — 코드위즈 고수준 디바이스 API (P2)
// 전송(WebSerial/릴레이) + REPL 클라이언트 + 멀티앱 파티션 제어를 묶는다.
// 블록→MicroPython 코드생성기(P6)는 device.run(code) 한 줄로 연결된다.

import type { Transport } from "./transport";
import { ReplClient, type ReplOptions } from "./replClient";
import { WebSerialTransport } from "./webSerialTransport";
import { RelayTransport, type RelayConfig } from "./relayTransport";

// T1 분석(PySerial): ESP32 멀티앱 파티션 전환 제어 프레임(0xC0 ... 0xC0)
// app0/app1/app2 각각 run/set. 펌웨어/마이크로파이썬/플레이팩이 파티션별 상주.
const FRAME = (op: number) =>
  new Uint8Array([0xc0, 0x00, op, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0]);
export const PARTITION = {
  runApp0: FRAME(0xe0),
  setApp0: FRAME(0xe1),
  runApp1: FRAME(0xe2), // MicroPython 파티션(일반적으로 여기 상주)
  setApp1: FRAME(0xe3),
  runApp2: FRAME(0xe4),
  setApp2: FRAME(0xe5),
} as const;

export type ConnectMode =
  | { via: "webserial" }
  | { via: "relay"; relay: RelayConfig };

export interface CodewizEvents extends ReplOptions {
  onStatus?: (s: "connecting" | "ready" | "running" | "stopped" | "disconnected") => void;
}

export class CodewizDevice {
  private transport: Transport | null = null;
  private repl: ReplClient | null = null;

  constructor(private events: CodewizEvents = {}) {}

  get connected() {
    return !!this.transport?.isOpen;
  }

  /** 연결: 모드에 맞는 전송을 열고, MicroPython 파티션(app1) 실행 후 REPL 준비 */
  async connect(mode: ConnectMode): Promise<void> {
    this.events.onStatus?.("connecting");
    this.transport =
      mode.via === "webserial"
        ? new WebSerialTransport()
        : new RelayTransport(mode.relay);
    await this.transport.open();

    this.repl = new ReplClient(this.transport, {
      onOutput: this.events.onOutput,
    });
    this.repl.attach();

    // MicroPython 파티션 실행(상주 모드 진입)
    await this.transport.write(PARTITION.runApp1);
    this.events.onStatus?.("ready");
  }

  /** 블록/파이썬 코드 즉시 실행(컴파일 없음) */
  async run(code: string): Promise<void> {
    this.assertReady();
    this.events.onStatus?.("running");
    await this.repl!.run(code);
  }

  /** 실행 정지 */
  async stop(): Promise<void> {
    this.assertReady();
    await this.repl!.stop();
    this.events.onStatus?.("stopped");
  }

  /** main.py 로 영구 저장(전원 재인가 후 자동 실행) */
  async save(code: string) {
    this.assertReady();
    return this.repl!.saveToMainPy(code);
  }

  /** 파티션 전환(고급) */
  async selectPartition(frame: Uint8Array) {
    this.assertReady();
    await this.transport!.write(frame);
  }

  async disconnect(): Promise<void> {
    this.repl?.release();
    await this.transport?.close();
    this.transport = null;
    this.repl = null;
    this.events.onStatus?.("disconnected");
  }

  /** 현재 환경에서 권장 연결 모드: WebSerial 지원 시 직결, 아니면(iPad 등) 릴레이 */
  static recommend(relay?: RelayConfig): ConnectMode {
    if (WebSerialTransport.supported) return { via: "webserial" };
    if (relay) return { via: "relay", relay };
    throw new Error("WebSerial 미지원 + 릴레이 설정 없음 — 연결 불가");
  }

  private assertReady() {
    if (!this.transport?.isOpen || !this.repl) throw new Error("코드위즈 미연결");
  }
}
